/**
 * Formatting decisions for the AI Intelligence Portfolio tab (`GET /ai-assets/portfolio`).
 * Extracted from the panel so the rules that keep absence from reading as zero are testable
 * without rendering anything.
 *
 * The response is either `{ withheld: true, reason }` or the portfolio, whose six sections are
 * each `{ data, unavailableReason, available }` with exactly one of data / reason set. A section
 * left null has not been built by the server. Reasons arrive as lower-case clauses; they are
 * rendered as sentences, never replaced by a 0, a dash or an empty bar.
 */
import { fmtMoneyAtLeast } from "./moneyFormat";

/** Used when a section is missing from the response altogether. */
export const SECTION_NOT_RETURNED_REASON =
  "this section was not returned by the server, so it is not known";

/** Used when a list came back empty with no reason attached: empty is not the same as complete. */
export const EMPTY_LIST_NOT_COMPLETE_REASON =
  "nothing was returned, which does not show that nothing is missing";

export const TOP_N = 10;

/** True when the server refused the estate-wide figures for this caller. */
export const isWithheld = (response) => response?.withheld === true;

/** A reason clause as a display sentence: first letter capitalised, closing full stop. */
export const asSentence = (reason) => {
  const text = String(reason ?? "").trim();
  if (!text) return "";
  const capitalised = text.charAt(0).toUpperCase() + text.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
};

/**
 * One section of the response, normalised: `{ available: true, data }` or
 * `{ available: false, reason }`. Never throws, whatever the server sent.
 */
export const sectionState = (section) => {
  if (!section || typeof section !== "object") {
    return { available: false, reason: asSentence(SECTION_NOT_RETURNED_REASON) };
  }
  if (section.available === false || section.data == null) {
    return {
      available: false,
      reason: asSentence(section.unavailableReason || SECTION_NOT_RETURNED_REASON),
    };
  }
  return { available: true, data: section.data };
};

/**
 * A figure that may be absent: `{ kind: "value", text }` when there is a number, otherwise
 * `{ kind: "reason", text }` carrying the sentence. A null value never becomes 0.
 */
export const figureOrReason = (value, reason, format = (v) => String(v)) => {
  if (value === null || value === undefined) {
    return { kind: "reason", text: asSentence(reason || SECTION_NOT_RETURNED_REASON) };
  }
  return { kind: "value", text: format(value) };
};

/** Exposure is a floor (only findings carrying an estimate are priced), hence the trailing "+". */
export const formatExposure = (value) => fmtMoneyAtLeast(value);

/**
 * The three no-remediation horizons beside the annual base: `{ rows, assumption }`, or null when
 * they are not to be shown at all. Null when the base total is null (its own reason is then the
 * only explanation on screen, so no slot and no second sentence) and when the assumption sentence
 * is missing (the figures never travel without it). Each row goes through `formatExposure`: a
 * projection of a floor is still a floor. A null horizon beside a present base renders a reason,
 * never 0. Cumulative expected loss, never summed with the usage forecast.
 */
export const exposureHorizons = (exposure) => {
  if (!exposure || exposure.totalExposure === null || exposure.totalExposure === undefined) return null;
  const assumption = String(exposure.exposureProjectionAssumption ?? "").trim();
  if (!assumption) return null;
  const rows = [
    { key: "45", days: 45, value: exposure.expectedLoss45Days },
    { key: "90", days: 90, value: exposure.expectedLoss90Days },
    { key: "180", days: 180, value: exposure.expectedLoss180Days },
  ].map((h) => ({
    key: h.key,
    label: `Cumulative expected loss — next ${h.days} days`,
    figure: figureOrReason(h.value, null, formatExposure),
  }));
  return { rows, assumption };
};

/** `{ count, total, share }` as "3 of 10 (30%)". The percentage is left off over an empty total. */
export const formatFraction = (fraction) => {
  if (!fraction) return null;
  const { count, total, share } = fraction;
  const base = `${count} of ${total}`;
  return share === null || share === undefined ? base : `${base} (${Math.round(share * 100)}%)`;
};

/** A 0–1 share as a whole percentage, or null when there is none. */
export const formatShare = (share) =>
  share === null || share === undefined ? null : `${Math.round(share * 100)}%`;

/**
 * Tier 1–3 are declared tiers. Anything else — no tier, or a value outside 1–3 — is Untiered,
 * never a fourth tier and never Tier 3. (The server buckets out-of-range values the same way.)
 */
export const tierLabel = (tier) =>
  tier === 1 || tier === 2 || tier === 3 ? `Tier ${tier}` : "Untiered";

/** The exposure-by-tier rows, untiered kept as its own row after the three declared tiers. */
export const tierExposureRows = (byTier) => {
  if (!byTier) return [];
  return [
    { key: "tier1", label: "Tier 1", value: byTier.tier1 },
    { key: "tier2", label: "Tier 2", value: byTier.tier2 },
    { key: "tier3", label: "Tier 3", value: byTier.tier3 },
    { key: "untiered", label: "Untiered (no tier declared)", value: byTier.untiered },
  ];
};

const byNumberDescThenId = (number, id) => (a, b) =>
  (number(b) ?? 0) - (number(a) ?? 0) || (id(a) ?? 0) - (id(b) ?? 0);

/** Top assets by exposure: exposure descending, asset id ascending, at most 10. */
export const topExposureAssets = (assets, n = TOP_N) =>
  [...(assets ?? [])]
    .sort(byNumberDescThenId((a) => a.exposure, (a) => a.assetId))
    .slice(0, n);

/** Top assets by reach: reach count descending, asset id ascending, at most 10. */
export const topReachAssets = (assets, n = TOP_N) =>
  [...(assets ?? [])]
    .sort(byNumberDescThenId((a) => a.reachCount, (a) => a.assetId))
    .slice(0, n);

/**
 * Shared endpoints by caller count descending. The sort is stable, so the server's own tie-break
 * (node key hash, then node id) survives. Pass `n = Infinity` to keep every row.
 */
export const orderSharedEndpoints = (endpoints, n = TOP_N) =>
  [...(endpoints ?? [])]
    .sort((a, b) => (b.callerCount ?? 0) - (a.callerCount ?? 0))
    .slice(0, n);

/** Ownership is set per endpoint by a person. Unreviewed is an open question, not "safe". */
export const ownershipLabel = (disposition) => {
  switch (disposition) {
    case "ThirdParty": return "Third-party operated";
    case "Operated": return "Operated by your organization";
    case "Unreviewed": return "Unreviewed (not yet classified)";
    default: return null;
  }
};

/** Data sensitivity on a data-source endpoint; unset is "not assessed", never low. */
export const sensitivityLabel = (endpoint) => {
  if (endpoint?.dataSensitivityNotAssessed) return "Sensitivity not assessed";
  return endpoint?.dataSensitivity ? `Sensitivity: ${endpoint.dataSensitivity}` : null;
};

const DIMENSION_LABELS = {
  AuthenticationRate: "Authentication rate",
  SpendRate: "Spend rate",
  DominantModel: "Dominant model",
  ResourceChurn: "Resource churn",
};

export const dimensionLabel = (dimension) => DIMENSION_LABELS[dimension] ?? dimension;

/** The envelope statuses, in the order they are shown. Keys are the server's strings verbatim. */
export const ENVELOPE_STATUS_ORDER = [
  { key: "Ready", label: "Ready" },
  { key: "Warming", label: "Warming (still learning normal)" },
  { key: "Unavailable", label: "Unavailable" },
  { key: "NotApplicable", label: "Not applicable" },
];

/**
 * One drift dimension as rows: the four status counts (a key the server left out is shown as not
 * reported, not as 0) and the withheld reasons with their asset counts, which are always shown
 * beside the counts because the counts cover evaluated in-region assets only.
 */
export const driftDimensionView = (dimension) => {
  const counts = dimension?.countsByStatus ?? {};
  return {
    label: dimensionLabel(dimension?.dimension),
    statuses: ENVELOPE_STATUS_ORDER.map(({ key, label }) => ({
      key,
      label,
      count: Object.prototype.hasOwnProperty.call(counts, key) ? counts[key] : null,
    })),
    withheld: (dimension?.withheldReasons ?? []).map((w) => ({
      reason: asSentence(w.reason),
      assetCount: w.assetCount,
    })),
  };
};

const FEED_STATUS_LABELS = {
  Healthy: "Healthy",
  NotConfigured: "Not configured",
  Unauthorized: "Unauthorized",
  Failed: "Failed",
};

export const feedStatusLabel = (status) => FEED_STATUS_LABELS[status] ?? status;

/**
 * A list that may legitimately be empty, with the sentence that says what empty means:
 * `{ rows, emptyReason }` where `emptyReason` is set only when there are no rows.
 */
export const listOrReason = (rows, emptyReason) => {
  const list = rows ?? [];
  if (list.length > 0) return { rows: list, emptyReason: null };
  return { rows: [], emptyReason: asSentence(emptyReason || EMPTY_LIST_NOT_COMPLETE_REASON) };
};

export const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
